import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Button, Alert, Spinner, Card } from 'react-bootstrap';
import {
  fetchCrmLeadsById,
  updateLead
} from '../../../services/crmLeadsService';

interface Lead {
  lead_id: number;
  crm_institute_id: number;
  priority: string;
  source: string;
  status: string;
  assigned_to: number;
  created_at: string;
  updated_at: string;
  institute: {
    institute_name: string;
  };
}

const EditLead: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    status: '',
    priority: '',
    source: ''
  });

  // Fetch Lead Details
  useEffect(() => {
    const loadLead = async () => {
      try {
        const data = await fetchCrmLeadsById(id);
        setLead(data);
        setFormData({
          status: data.status,
          priority: data.priority,
          source: data.source
        });
      } catch (error) {
        setError('Failed to fetch lead details.');
      } finally {
        setLoading(false);
      }
    };
    loadLead();
  }, [id]);

  // Handle Input Changes
  const handleChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle Form Submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await updateLead(id, formData);
      alert('Lead updated successfully!');
      navigate('/crm/leads/all-leads');
    } catch (error) {
      setError('Failed to update lead.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
        <p>Loading Lead Details...</p>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div className="container mt-4">
      <Card className="shadow-lg p-4">
        <Card.Body>
          <Card.Title>Edit Lead</Card.Title>
          <Card.Subtitle className="mb-3 text-muted">
            Institute: <strong>{lead?.institute?.institute_name}</strong>
          </Card.Subtitle>

          <Form onSubmit={handleSubmit}>
            {/* Priority */}
            <Form.Group className="mb-3">
              <Form.Label>Priority</Form.Label>
              <Form.Select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                required
              >
                <option value="">Select Priority</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </Form.Select>
            </Form.Group>

            {/* Source */}
            <Form.Group className="mb-3">
              <Form.Label>Source</Form.Label>
              <Form.Select
                name="source"
                value={formData.source}
                onChange={handleChange}
                required
              >
                <option value="">Select Source</option>
                <option value="Referral">Referral</option>
                <option value="Website">Website</option>
                <option value="Event">Event</option>
              </Form.Select>
            </Form.Group>

            {/* Status */}
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
              >
                <option value="">Select Status</option>
                <option value="Open">Open</option>
                <option value="Closed">Closed</option>
                <option value="Converted">Converted</option>
              </Form.Select>
            </Form.Group>

            {/* Submit Button */}
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <Spinner animation="border" size="sm" />
              ) : (
                'Update Lead'
              )}
            </Button>

            {/* Back Button */}
            <Button
              variant="secondary"
              className="ms-2"
              onClick={() => navigate('/crm/leads')}
            >
              Cancel
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default EditLead;
